// ---------------------------------------------------------------------------
// DataSourceTabs -- Inline Pill Group
// ---------------------------------------------------------------------------
// Replaces DataSourceDropdown with an always-visible pill/tab group.
// The active datasource gets a filled primary pill; inactive ones are muted.
// ---------------------------------------------------------------------------

import React, { useCallback } from 'react';
import { cn } from '@/lib/utils';
import { DATA_SOURCES } from './constants';
import type { DataSourceId } from './types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DataSourceTabsProps {
  readonly selectedSource: DataSourceId;
  readonly onSourceChange: (source: DataSourceId) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function DataSourceTabs({ selectedSource, onSourceChange }: DataSourceTabsProps) {
  const handleSelect = useCallback(
    (sourceId: DataSourceId) => {
      if (sourceId !== selectedSource) {
        onSourceChange(sourceId);
      }
    },
    [selectedSource, onSourceChange],
  );

  return (
    <div
      data-testid="data-source-tabs"
      className="flex items-center bg-v11-surface border border-v11-border rounded-lg p-0.5 gap-0.5"
    >
      {DATA_SOURCES.map((source) => {
        const isActive = source.id === selectedSource;

        return (
          <button
            key={source.id}
            type="button"
            data-testid={`data-source-tab-${source.id}`}
            onClick={() => handleSelect(source.id)}
            className={cn(
              'px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap',
              isActive
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-v11-text-secondary hover:text-v11-text-primary hover:bg-v11-surface-hover',
            )}
          >
            {source.label}
          </button>
        );
      })}
    </div>
  );
}

export { DataSourceTabs };
export type { DataSourceTabsProps };
